import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Rss } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import CreditUsageWidget from "@/components/CreditUsageWidget";
import { SandboxBanner, StatusBadge } from "@/components/outreach/shared";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function OutreachCampaigns() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [meta, setMeta] = useState(null);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [c, m] = await Promise.all([api.outreachCampaigns(100, 0), api.outreachMeta()]);
                setItems(c.items || []);
                setMeta(m);
            } finally { setLoading(false); }
        })();
    }, []);

    if (!user) return null;

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(c => !c)} />
            <div className="flex-1 min-w-0 flex flex-col">
                <Header />
                <main data-testid="campaigns-main" className="flex-1 px-4 md:px-6 py-6 max-w-6xl w-full">
                    <SandboxBanner show={meta?.sandbox} />
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
                        <div>
                            <p className="text-[11px] uppercase tracking-widest text-text-secondary">Campaigns</p>
                            <h1 className="mt-1 text-2xl md:text-3xl font-semibold tracking-tight">
                                Tutte le tue{" "}
                                <span className="gradient-text">campagne</span>
                            </h1>
                        </div>
                        <div className="flex items-center gap-2">
                            <CreditUsageWidget />
                            <Link
                                to="/outreach/campaigns/new"
                                data-testid="campaigns-new-btn"
                                className="inline-flex items-center gap-2 rounded-full gradient-bg-cta px-5 py-2.5 text-sm font-medium text-white"
                            >
                                <Plus className="h-4 w-4" /> New
                            </Link>
                        </div>
                    </div>

                    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
                        {loading ? (
                            <div className="p-6 text-sm text-text-secondary">Loading…</div>
                        ) : items.length === 0 ? (
                            <div data-testid="campaigns-empty" className="p-12 text-center">
                                <div className="mx-auto h-14 w-14 rounded-full bg-[var(--color-panel-ai)] grid place-items-center border border-[var(--color-border)]">
                                    <Rss className="h-5 w-5 text-[var(--color-secondary)]" />
                                </div>
                                <h3 className="mt-4 text-base font-semibold">Nessuna campagna</h3>
                                <p className="mt-2 text-sm text-text-secondary">Crea la prima campagna dal wizard.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="text-[11px] uppercase tracking-widest text-text-secondary/80 border-b border-[var(--color-border)]">
                                        <tr>
                                            <th className="text-left px-4 py-2 font-normal">Name</th>
                                            <th className="text-left px-4 py-2 font-normal">Status</th>
                                            <th className="text-right px-4 py-2 font-normal">Sent</th>
                                            <th className="text-right px-4 py-2 font-normal">Opened</th>
                                            <th className="text-right px-4 py-2 font-normal">Clicked</th>
                                            <th className="text-right px-4 py-2 font-normal">Bounced</th>
                                            <th className="text-right px-4 py-2 font-normal">Unsub</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((c, i) => (
                                            <motion.tr
                                                key={c.id}
                                                initial={{ opacity: 0, y: 2 }} animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.2, delay: i * 0.02 }}
                                                data-testid={`campaign-row-${i}`}
                                                className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-panel-ai)]/50 transition-colors"
                                            >
                                                <td className="px-4 py-3">
                                                    <Link to={`/outreach/campaigns/${c.id}`} className="font-medium hover:text-[var(--color-secondary)]">
                                                        {c.name}
                                                    </Link>
                                                </td>
                                                <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                                                <td className="px-4 py-3 text-right tabular-nums">{c.stats?.sent || 0}</td>
                                                <td className="px-4 py-3 text-right tabular-nums">{c.stats?.opened || 0}</td>
                                                <td className="px-4 py-3 text-right tabular-nums">{c.stats?.clicked || 0}</td>
                                                <td className="px-4 py-3 text-right tabular-nums">{c.stats?.bounced || 0}</td>
                                                <td className="px-4 py-3 text-right tabular-nums">{c.stats?.unsubscribed || 0}</td>
                                            </motion.tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
}
